import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Share2,
  BookOpen,
  MapPin,
  Tag,
  Phone,
  Globe,
  Mail,
  HelpCircle,
  Plane,
  DollarSign,
} from "lucide-react";
import UserNavbar from "components/UserNavbar/UserNavbar";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import blogPosts from "../log/BlogData";

// Default SEO data for fallback
const DEFAULT_SEO = {
  title: "Maharaja Flights Limited® | Travel Blog",
  description:
    "Discover travel tips, destination guides, airline insights, and booking advice from Maharaja Flights Limited® - Your trusted travel partner.",
  keywords:
    "travel, flights, canada to india, travel agency, tips, booking, destinations",
  image: "https://maharajaflights.com/default-og-image.jpg",
  siteUrl: "https://maharajaflights.com",
  siteName: "Maharaja Flights Limited®",
  twitterHandle: "@MaharajaFlights",
  phone: "+1-855-243-1300",
  email: "info@maharajaflights.com",
  address: "20 Bay Street, Toronto, Canada",
};

export default function Bloginfo() {
  const { slug } = useParams();
  const location = useLocation();
  const [post, setPost] = useState(location.state);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Generate canonical URL
  const canonicalUrl = `${DEFAULT_SEO.siteUrl}/blog/${slug}`;

  // JSON-LD Schema Generator
  const generateArticleSchema = (postData) => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: postData.seoTitle || postData.title,
      description:
        postData.seoDescription ||
        postData.metaDescription ||
        postData.description,
      image: postData.imglg,
      author: {
        "@type": "Person",
        name: postData.author || "Travel Expert",
      },
      publisher: {
        "@type": "Organization",
        name: DEFAULT_SEO.siteName,
        logo: {
          "@type": "ImageObject",
          url: `${DEFAULT_SEO.siteUrl}/logo.png`,
        },
        contactPoint: {
          "@type": "ContactPoint",
          telephone: DEFAULT_SEO.phone,
          contactType: "customer service",
          areaServed: "CA",
          availableLanguage: "en",
        },
      },
      datePublished: postData.date || new Date().toISOString(),
      dateModified: postData.date || new Date().toISOString(),
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": canonicalUrl,
      },
    };

    if (postData.readTime) {
      schema.timeRequired = `PT${postData.readTime.split(" ")[0]}M`;
    }

    return JSON.stringify(schema);
  };

  // Breadcrumb Schema
  const generateBreadcrumbSchema = (postTitle) => {
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: DEFAULT_SEO.siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: `${DEFAULT_SEO.siteUrl}/blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: postTitle,
          item: canonicalUrl,
        },
      ],
    });
  };

  // Generate Organization Schema
  const generateOrganizationSchema = () => {
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TravelAgency",
      name: DEFAULT_SEO.siteName,
      description: DEFAULT_SEO.description,
      url: DEFAULT_SEO.siteUrl,
      logo: `${DEFAULT_SEO.siteUrl}/logo.png`,
      telephone: DEFAULT_SEO.phone,
      email: DEFAULT_SEO.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: "20 Bay Street",
        addressLocality: "Toronto",
        addressCountry: "CA",
      },
      priceRange: "$$",
      sameAs: [
        "https://www.facebook.com/MaharajaFlights",
        "https://www.twitter.com/MaharajaFlights",
        "https://www.instagram.com/MaharajaFlights",
      ],
    });
  };

  // FAQ Schema Generator
  const generateFAQSchema = (faqs) => {
    if (!faqs || faqs.length === 0) return null;

    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq, index) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });
  };

  // If navigated directly (no state), find post by slug
  useEffect(() => {
    if (!post) {
      const found = blogPosts?.find(
        (p) =>
          p.title
            .toLowerCase()
            .trim()
            .replace(/\s+/g, "-")
            .replace(/[^\w\-]/g, "") === slug
      );
      setTimeout(() => {
        setPost(found);
        setIsLoading(false);
      }, 500);
    } else {
      setIsLoading(false);
    }
  }, [post, slug]);

  // Format date if your post data has date
  const formatDate = (dateString) => {
    if (!dateString) return "Recent";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Share functionality
  const sharePost = async () => {
    if (!post) return;

    const shareData = {
      title: post.seoTitle || post.title,
      text: post.seoDescription || post.metaDescription || post.description,
      url: window.location.href,
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log("Error sharing:", err);
      }
    } else {
      // Fallback copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Check if post is about Maharaja Flights
  const isMaharajaPost =
    post?.tags?.some(
      (tag) =>
        tag.toLowerCase().includes("maharaja") ||
        tag.toLowerCase().includes("travel-agent") ||
        tag.toLowerCase().includes("agency")
    ) ||
    post?.category?.toLowerCase().includes("company") ||
    post?.category?.toLowerCase().includes("local") ||
    post?.title?.toLowerCase().includes("maharaja");

  // Check if post is about booking/flights
  const isBookingPost =
    post?.category?.toLowerCase().includes("booking") ||
    post?.tags?.some(
      (tag) =>
        tag.toLowerCase().includes("booking") ||
        tag.toLowerCase().includes("fare") ||
        tag.toLowerCase().includes("search")
    );

  if (isLoading) {
    return (
      <HelmetProvider>
        <section className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
          <Helmet>
            <title>Loading... | {DEFAULT_SEO.siteName}</title>
            <meta name="description" content={DEFAULT_SEO.description} />
          </Helmet>
          <UserNavbar />
          <div className="mx-auto max-w-4xl px-4 py-20">
            <div className="flex flex-col items-center justify-center">
              <div className="mb-6 h-8 w-64 animate-pulse rounded-lg bg-gray-300"></div>
              <div className="mb-8 h-96 w-full animate-pulse rounded-2xl bg-gray-300"></div>
              <div className="w-full space-y-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-32 animate-pulse rounded-xl bg-gray-300"
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </HelmetProvider>
    );
  }

  if (!post) {
    return (
      <HelmetProvider>
        <section className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
          <Helmet>
            <title>Post Not Found | {DEFAULT_SEO.siteName}</title>
            <meta
              name="description"
              content="The blog post you're looking for doesn't exist."
            />
            <meta name="robots" content="noindex, follow" />
          </Helmet>
          <UserNavbar />
          <div className="mx-auto max-w-2xl px-4 py-20 text-center">
            <h1 className="mb-4 text-3xl font-bold text-gray-900">
              Post Not Found
            </h1>
            <p className="mb-8 text-gray-600">
              The blog post you're looking for doesn't exist.
            </p>
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-6 py-3 font-semibold text-white transition-all hover:bg-red-600 hover:shadow-lg"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
          </div>
        </section>
      </HelmetProvider>
    );
  }

  const {
    imglg,
    content,
    title,
    seoTitle = title,
    seoDescription = post.metaDescription ||
      post.description ||
      DEFAULT_SEO.description,
    author = "Travel Expert",
    date = new Date().toISOString(),
    readTime = "5 min",
    category = "Travel",
    tags = [],
    featuredImageAlt = title,
    faqs = [],
  } = post;

  // Internal link mapping
  const internalLinks = {
    "international flights": "/flights",
    "business class tickets": "/business-class",
    "last-minute travel": "/last-minute-flights",
    "travel agent assistance": "/contact-us",
    "flight booking": "/flights",
    "multi-city itineraries": "/multi-city",
    "Maharaja Flights Limited®": "/",
    "travel agent": "/contact-us",
    agent: "/contact-us",
    "call us": "/contact-us",
    "contact us": "/contact-us",
  };

  // Function to render content with internal links
  const renderContentWithLinks = (text) => {
    let processedText = text;
    Object.entries(internalLinks).forEach(([key, link]) => {
      const regex = new RegExp(`(${key})`, "gi");
      processedText = processedText.replace(
        regex,
        `<a href="${link}" class="text-red-600 font-medium hover:text-red-700 underline transition-colors">$1</a>`
      );
    });
    return processedText;
  };

  return (
    <HelmetProvider>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={tags.join(", ")} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={imglg} />
        <meta property="og:image:alt" content={featuredImageAlt} />
        <meta property="og:site_name" content={DEFAULT_SEO.siteName} />
        <meta property="article:published_time" content={date} />
        <meta property="article:author" content={author} />
        {tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={imglg} />
        <meta name="twitter:image:alt" content={featuredImageAlt} />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Additional Meta Tags */}
        <meta name="author" content={author} />
        <meta name="publish_date" property="og:publish_date" content={date} />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:label1" content="Written by" />
        <meta name="twitter:data1" content={author} />
        <meta name="twitter:label2" content="Est. reading time" />
        <meta name="twitter:data2" content={readTime} />
      </Helmet>

      <section className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <UserNavbar />
        <ScrollToTopOnRouteChange />

        {/* Back Navigation */}
        <div className="border-b border-gray-200 bg-white/80 backdrop-blur-sm">
          <div className="mx-auto max-w-8xl px-4 py-4">
            <div className="flex items-center justify-between">
              <Link
                to="/blogs"
                className="inline-flex items-center gap-2 text-gray-600 transition-colors hover:text-red-500"
              >
                <ArrowLeft size={20} />
                <span className="font-medium">Back to Blog</span>
              </Link>

              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="hidden sm:inline">Published:</span>
                <Calendar size={14} />
                <span>{formatDate(date)}</span>
              </div>
            </div>
          </div>
        </div>

        <article className="mx-auto max-w-8xl px-4 py-8">
          {/* Article Header */}
          <header className="mb-10">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-red-50 to-orange-50 px-4 py-2 text-sm font-medium text-red-600">
                {isBookingPost ? (
                  <DollarSign size={14} />
                ) : (
                  <MapPin size={14} />
                )}
                {category}
              </span>
              {tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-600"
                >
                  <Tag size={12} />
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mb-6 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-4xl font-bold leading-tight text-transparent sm:text-5xl md:text-6xl">
              {title}
            </h1>

            <div className="mb-8">
              <p className="text-lg text-gray-600 md:text-xl">
                {seoDescription}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-6 border-y border-gray-200 py-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-red-100 to-orange-100">
                  <User size={24} className="text-red-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{author}</p>
                  <p className="text-sm text-gray-500">
                    {isMaharajaPost
                      ? "Maharaja Flights Limited® Team"
                      : isBookingPost
                      ? "Booking Expert"
                      : "Travel Writer"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={18} />
                  <span>{readTime} read</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={sharePost}
                    className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 transition-all hover:bg-gray-200 hover:shadow-md"
                  >
                    <Share2 size={18} />
                    {copied ? "Copied!" : "Share"}
                  </button>
                </div>
              </div>
            </div>
          </header>
            {/* Featured Image */}
            <div className="mb-12 overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 hover:shadow-3xl">
              <img
                src={imglg}
                alt={featuredImageAlt}
                className="h-auto w-full object-cover transition-transform duration-700 hover:scale-100 sm:max-h-[500px]"
                loading="lazy"
                title={featuredImageAlt}
              />
              <p className="mt-2 text-center text-sm text-gray-900">
                {featuredImageAlt}
              </p>
            </div>

            {/* Article Content */}
            <div className="prose-lg prose max-w-none space-y-12">
              {content.map((section, index) => (
                <div
                  key={index}
                  className={`rounded-2xl p-8 transition-all hover:shadow-lg ${
                    index % 2 === 0
                      ? "bg-white"
                      : "bg-gradient-to-br from-white to-gray-50"
                  }`}
                >
                  <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-red-500 to-orange-500 text-white">
                      <span className="text-xl font-bold">{index + 1}</span>
                    </div>
                    <h2 className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-2xl font-bold text-transparent md:text-3xl">
                      {section.title}
                    </h2>
                  </div>

                  <ul className="space-y-5 pl-2">
                    {section.points.map((point, idx) => {
                      const hasColon = point.includes(":");
                      const isListPoint =
                        point.startsWith("•") || point.startsWith("-");
                      const isCheckmark =
                        point.includes("✅") || point.includes("✔");
                      const isImportant =
                        point.includes("📞") ||
                        point.includes("📲") ||
                        point.includes("🌍");

                      return (
                        <li
                          key={idx}
                          className="group relative flex items-start gap-4 pl-8 before:absolute before:left-0 before:top-3 before:h-2 before:w-2 before:rounded-full before:bg-gradient-to-r before:from-red-400 before:to-orange-400 before:transition-all before:group-hover:h-3 before:group-hover:w-3"
                        >
                          <div
                            className={`w-full rounded-lg bg-gradient-to-br p-5 shadow-sm transition-all group-hover:shadow-md ${
                              isCheckmark
                                ? "to-emerald-50 border border-green-100 from-green-50"
                                : isImportant
                                ? "border border-blue-100 from-blue-50 to-cyan-50"
                                : "from-gray-50 to-white"
                            }`}
                          >
                            {hasColon ? (
                              <p
                                className="leading-relaxed text-gray-700"
                                dangerouslySetInnerHTML={{
                                  __html: renderContentWithLinks(
                                    `<span class="mr-2 bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text font-semibold text-transparent">${
                                      point.split(":")[0]
                                    }:</span>${point.split(":")[1]}`
                                  ),
                                }}
                              />
                            ) : isListPoint || isCheckmark || isImportant ? (
                              <p
                                className="leading-relaxed text-gray-700"
                                dangerouslySetInnerHTML={{
                                  __html: renderContentWithLinks(point),
                                }}
                              />
                            ) : (
                              <p
                                className="leading-relaxed text-gray-700"
                                dangerouslySetInnerHTML={{
                                  __html: renderContentWithLinks(point),
                                }}
                              />
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

          {/* FAQ Section */}
          {faqs.length > 0 && (
            <div className="mt-12">
              <h3 className="mb-6 flex items-center gap-2 text-2xl font-bold text-gray-900">
                <HelpCircle size={24} />
                Frequently Asked Questions
              </h3>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-red-300 hover:shadow-md"
                  >
                    <h4 className="mb-3 text-lg font-semibold text-gray-900">
                      {faq.question}
                    </h4>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags Section */}
          {tags.length > 0 && (
            <div className="mt-12">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Tag size={18} />
                Article Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-gray-100 px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-200"
                  >
                    #{tag.replace(/\s+/g, "-")}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Article Footer */}
          <footer className="mt-16 rounded-2xl bg-gradient-to-r from-red-50 to-orange-50 p-8">
            <div className="mb-6 flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-500">
                {isBookingPost ? <Plane size={28} /> : <BookOpen size={28} />}
                <span className="sr-only">Icon</span>
              </div>
            </div>
            <h3 className="mb-4 text-center text-2xl font-bold text-gray-900">
              {isMaharajaPost
                ? "Ready to book your journey?"
                : "Thanks for reading!"}
            </h3>
            <p className="mb-6 text-center text-gray-600">
              {isMaharajaPost
                ? "Contact Maharaja Flights Limited® for personalized service, exclusive fares, and 24/7 support."
                : isBookingPost
                ? "Ready to find your perfect flight? Use these tips to get the best deals and travel smarter."
                : "Found this article helpful? Share it with others who might benefit from these insights."}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={sharePost}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 font-semibold text-gray-800 shadow-md transition-all hover:shadow-lg"
              >
                <Share2 size={20} />
                Share Article
              </button>
              <Link
                to="/blogs"
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 px-6 py-3 font-semibold text-white shadow-md transition-all hover:shadow-lg"
              >
                <ArrowLeft size={20} />
                More Articles
              </Link>
              {isMaharajaPost && (
                <a
                  href="tel:+18552431300"
                  className="inline-flex items-center gap-2 rounded-lg bg-green-500 px-6 py-3 font-semibold text-white shadow-md transition-all hover:shadow-lg"
                >
                  <Phone size={20} />
                  Call Now
                </a>
              )}
              {isBookingPost && (
                <Link
                  to="/flights"
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-6 py-3 font-semibold text-white shadow-md transition-all hover:shadow-lg"
                >
                  <Plane size={20} />
                  Search Flights
                </Link>
              )}
            </div>

            {/* Author Bio */}
            <div className="mt-8 border-t border-gray-200 pt-8">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-red-100 to-orange-100">
                  <User size={28} className="text-red-500" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">
                    About {isMaharajaPost ? "Maharaja Flights Limited®" : author}
                  </h4>
                  <p className="mt-2 text-gray-600">
                    {isMaharajaPost
                      ? "Maharaja Flights Limited® is a TICO-certified travel agency based in Toronto, specializing in flights from Canada to India, Pakistan, Nepal, and Asia. With 27+ years of experience, we provide personalized service, 24/7 support, and the best flight deals for our customers."
                      : isBookingPost
                      ? `${author} specializes in flight booking strategies, fare comparisons, and helping travelers find the best value for their journeys. With expertise in both online tools and traditional booking methods, they provide comprehensive guidance for modern travelers.`
                      : `${author} is a passionate travel writer with years of experience exploring destinations worldwide. They specialize in creating comprehensive guides that help travelers make the most of their journeys.`}
                  </p>
                  {isMaharajaPost && (
                    <div className="mt-4 flex flex-wrap gap-4 text-sm">
                      <a
                        href="tel:+18552431300"
                        className="flex items-center gap-2 text-gray-600 hover:text-red-500"
                      >
                        <Phone size={16} />
                        {DEFAULT_SEO.phone}
                      </a>
                      <a
                        href="mailto:info@maharajaflights.com"
                        className="flex items-center gap-2 text-gray-600 hover:text-red-500"
                      >
                        <Mail size={16} />
                        info@maharajaflights.com
                      </a>
                      <a
                        href="https://maharajaflights.com"
                        className="flex items-center gap-2 text-gray-600 hover:text-red-500"
                      >
                        <Globe size={16} />
                        www.maharajaflights.com
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </footer>
        </article>

        {/* Floating Back to Top */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-8 right-8 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-500 text-white shadow-xl transition-all hover:scale-110 hover:shadow-2xl"
          aria-label="Scroll to top"
        >
          <ArrowLeft className="rotate-90" size={24} />
        </button>
      </section>

      {/* Structured Data Scripts */}
      {post && (
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: generateArticleSchema(post) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: generateBreadcrumbSchema(seoTitle),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: generateOrganizationSchema() }}
          />
          {faqs.length > 0 && generateFAQSchema(faqs) && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: generateFAQSchema(faqs) }}
            />
          )}
        </>
      )}
    </HelmetProvider>
  );
}
